import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import SummaryItem from './SummaryItem';

// Components

import SummaryFilter from '@components/ssap/pages/pageIncludes/summary/SummaryFilter';
import PageHeader from '@components/ssap/headers/PageHeader';
import SourcesSummary from './summaryIncludes/SourcesSummary';
import StudentLoanInterestHelpBanner from './StudentLoanInterestHelpBanner';
import SubHeader from '../../../headers/SubHeader';

// Selectors

import {
    selectIfFinalReviewPageActive,
    selectIfSsapInReviewMode,
    selectIsInlineCurrentIncomeEnabled
} from '@reducers/appReducer';

// Utils

import { helperFunctions } from '@utils/tools/helperFunctions';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { transformDropdownOptionsToObject } from '@utils/normalize/transformDropdownOptionsToObject';
import { hasHouseholdStudentLoanInterestExpense } from '@utils/ssap/expenseUtils';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { INCOME_TYPES } from '@constants/ssap/hhmTypes/incomeAndExpenceTypes';
import { NOT_PROVIDED } from '@constants/ssap/miscellaneous/miscellaneous';
import { PAGES } from '@constants/ssap/flows/pagesTypes';

// Types

import { IncomeType, SsapSummaryType } from '@components/ssap/SsapTypes';


const IncomeInformationSummary = props => {

    const {
        coverageYear,
        buttonContainer: ButtonContainer,
        householdMembers,
        indexMapper,
        isState,
        isUnemploymentIncomeAttestationEnabled,
        itfFeatureEnabled,
        pageName,
        previousData,
        taxHousehold: {
            unemploymentIncome
        }
    } = props;

    const intlApi = useIntl();
    const inlineCurrentIncomeEnabled = useSelector(selectIsInlineCurrentIncomeEnabled);
    const isFinalSummaryPage = useSelector(selectIfFinalReviewPageActive);
    const isSsapInReviewMode = useSelector(selectIfSsapInReviewMode);

    const hasHouseholdStudentLoanInterestDeduction = hasHouseholdStudentLoanInterestExpense(householdMembers);

    const isIncomeSummaryPage = pageName === PAGES.INCOME_INFORMATION_SUMMARY_PAGE;
    const isReviewAndConfirmPage =
        pageName === PAGES.FINAL_APPLICATION_REVIEW_PAGE
        || pageName === PAGES.EXTRA_FINAL_APPLICATION_REVIEW_PAGE;

    const shouldShowStudentLoanInterestHelp =
        hasHouseholdStudentLoanInterestDeduction
        && (isIncomeSummaryPage || isReviewAndConfirmPage);

    const renderStudentLoanInterestHelpBanner = () => {
        if (!shouldShowStudentLoanInterestHelp) {
            return null;
        }

        const dataTestId = isReviewAndConfirmPage
            ? 'student-loan-interest-review-income-summary-help'
            : 'student-loan-interest-household-income-summary-help';

        return (
            <div className='grid-row'>
                <StudentLoanInterestHelpBanner dataTestId={dataTestId} />
            </div>
        );
    };

    const getHouseholdMembersSummaries = () => {
        return householdMembers.map((hhm , hhmIdx)=> {

            const {
                applicantGuid,
                name,
                annualTaxIncome: {
                    projectedIncome,
                    reportedIncome
                },
                incomes,
                expenses,
                taxHouseholdComposition: {
                    independentTaxFiler
                }
            } = hhm;

            const {
                firstName,
                lastName
            } = name;

            const isIndependentTaxFilerWarningIsShown = itfFeatureEnabled
                && independentTaxFiler
                && !!(projectedIncome || reportedIncome)
                && pageName !== PAGES.FINAL_APPLICATION_REVIEW_PAGE;

            const individualReasonsToRender = incomes.reduce((incomesToRender, income, incomeIdx) => {
                return income.incomeDifferenceReason
                    ? [
                        ...incomesToRender,
                        {
                            ...income,
                            incomeIdx
                        }
                    ]
                    : incomesToRender;
            }, []);

            const transformedOptions = transformDropdownOptionsToObject(DROPDOWN_OPTIONS.INCOME_INDIVIDUAL_DISCREPANCIES);

            const getReason = reason => {
                return transformedOptions[reason];
            };

            const getReasonByTypeOrSource = (incomes = [], sourceName, type) => {
                if(type === IncomeType.DUMMY) {
                    const incomeWithDummyType = incomes.find(i => i.type === IncomeType.DUMMY);
                    if(incomeWithDummyType) {
                        return getReason(incomeWithDummyType.incomeDifferenceReason);
                    } else {
                        return NOT_PROVIDED;
                    }
                }

                if(type === IncomeType.JOB) {
                    const incomeWithSameSource = incomes.find(i => i.sourceName === sourceName);
                    if(incomeWithSameSource) {
                        return getReason(incomeWithSameSource.incomeDifferenceReason);
                    } else {
                        return NOT_PROVIDED;
                    }
                }
            };

            const getIndividualReasons = () => {
                if(!inlineCurrentIncomeEnabled || isState.MN) {
                    return null;
                }

                return individualReasonsToRender.map(income => {
                    const {
                        incomeDifferenceReason,
                        sourceName,
                        type
                    } = income;

                    return (
                        <div
                            className = 'grid-row'
                            key = { `${sourceName}_${incomeDifferenceReason}` }
                        >
                            <SummaryItem
                                textCode = 'ssap.income_information.summary.individual_income_discrepancy'
                                replacements = { {
                                    isDummy: type === INCOME_TYPES.DUMMY,
                                    sourceName: <strong>{ sourceName }</strong>
                                } }
                                value = { getReason(incomeDifferenceReason) }
                                desc = {
                                    [
                                        [
                                            [
                                                `taxHousehold.householdMember[${ hhmIdx }].incomes`
                                            ],
                                            getReasonByTypeOrSource,
                                            [
                                                sourceName,
                                                type
                                            ]
                                        ]
                                    ]
                                }
                            />
                        </div>
                    );
                });
            };

            return (
                <SummaryFilter
                    key = { applicantGuid }
                    indexMapper = { indexMapper }
                    summaryId = { SsapSummaryType.INCOME_INFORMATION.processReplacements({ idx: hhmIdx }) }
                >
                    <div className = 'summary__block margin-b-20'>
                        <div className = 'summary__header'>
                            <SubHeader
                                dataTestId={`Income-Info-Header-${applicantGuid}`}
                                text = 'ssap.subheader.non_internationalized_text'
                                textReplacements = { {
                                    text: normalizeNameOfPerson(name)
                                } }
                                headerClassName = 'gi-heading-alt'
                                additionalElement = {(
                                    <ButtonContainer
                                        type = 'editButton_secondary'
                                        args = { [ hhmIdx, PAGES.INCOME_SOURCES_PAGE ] }
                                        ariaLabel={
                                            intlApi.formatMessage(
                                                { id: 'ssap.edit.income_information' },
                                                { firstName: name.firstName, lastName: name.lastName },
                                            )
                                        }
                                    />
                                )}
                                additionalElementClassName = 'header-btn'
                            />
                        </div>
                        <div className = 'grid-row' >
                            <SummaryItem
                                category = 'ssap.income_information.summary.yearly_income'
                                replacements = { {
                                    coverageYear
                                } }
                                value = { helperFunctions.getIncomeValueForRender(projectedIncome, reportedIncome) }
                                ignoreIntl
                                id = { `incomeSource_${firstName}_${lastName}` }
                                desc = {
                                    [
                                        [
                                            [
                                                `taxHousehold.householdMember[${ hhmIdx }].annualTaxIncome.projectedIncome`,
                                                `taxHousehold.householdMember[${ hhmIdx }].annualTaxIncome.reportedIncome`
                                            ],
                                            helperFunctions.getIncomeValueForRender
                                        ]
                                    ]
                                }
                            />
                        </div>

                        {
                            getIndividualReasons()
                        }

                        <SourcesSummary
                            { ...{
                                applicantGuid,
                                incomes,
                                expenses,
                                isState,
                                previousData,
                                reportedIncome,
                                isFinalSummaryPage,
                                isSsapInReviewMode
                            } }
                        />

                        {
                            isIndependentTaxFilerWarningIsShown &&

                            <div className = 'usa-alert gi-alert gi-alert__left-accent gi-alert__info'>
                                <div className = 'gi-alert__body'>
                                    <p className = 'gi-alert__text'>
                                        <FormattedMessage
                                            id = 'ssap.income_information.summary.independent_tax_filer'
                                            values = { {
                                                independentTaxFilerName: (
                                                    <b>
                                                        { normalizeNameOfPerson(name) }
                                                    </b>
                                                )
                                            } }
                                        />
                                    </p>
                                </div>
                            </div>
                        }
                    </div>
                </SummaryFilter>
            );
        });
    };

    const getTaxHouseholdIncomeDifferenceReason = reason => {
        const discrepancyOptions = isState.MN
            ? DROPDOWN_OPTIONS.INCOME_TAX_HOUSEHOLD_DISCREPANCIES_MN
            : DROPDOWN_OPTIONS.INCOME_TAX_HOUSEHOLD_DISCREPANCIES;
        const transformedOptions = transformDropdownOptionsToObject(discrepancyOptions);

        return transformedOptions[reason];
    };

    const getTaxHouseholdDiscrepancyBlock = () => {
        const hhmsWithTaxHouseholdIncomeDiscrepancy = householdMembers.reduce(
            (hhmsWithTaxHouseholdIncomeDiscrepancies, hhm, hhmIdx) => {
                const {
                    annualTaxIncome: {
                        incomeExplanationRequiredIndicator,
                        taxHouseholdIncomeDifferenceReason
                    },
                    name
                } = hhm;

                // Checked with Backend engineer that taxHouseholdIncomeDifferenceReason initial value is null
                if(incomeExplanationRequiredIndicator && taxHouseholdIncomeDifferenceReason !== null) {
                    const filerName = normalizeNameOfPerson(name);

                    return [
                        ...hhmsWithTaxHouseholdIncomeDiscrepancies,
                        {
                            filerName,
                            hhmIdx,
                            taxHouseholdIncomeDifferenceReason
                        }
                    ];
                } else {
                    return hhmsWithTaxHouseholdIncomeDiscrepancies;
                }
            },
            [ ]
        );


        if(!inlineCurrentIncomeEnabled || hhmsWithTaxHouseholdIncomeDiscrepancy.length === 0) {
            return null;
        }

        const getHhmsWithTaxHouseholdIncomeDiscrepancies = () => {
            return hhmsWithTaxHouseholdIncomeDiscrepancy.map(taxHouseholdDiscrepancy => {
                const {
                    filerName,
                    hhmIdx,
                    taxHouseholdIncomeDifferenceReason
                } = taxHouseholdDiscrepancy;

                return (
                    <div
                        className = 'grid-row'
                        key = { `tax_household_reason_${ hhmIdx }` }
                    >
                        <SummaryItem
                            textCode = 'ssap.income_information.summary.tax_household_income_discrepancy'
                            value = { getTaxHouseholdIncomeDifferenceReason(taxHouseholdIncomeDifferenceReason) }
                            id = 'tax_household_reason'
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ hhmIdx }].annualTaxIncome.taxHouseholdIncomeDifferenceReason`,
                                        getTaxHouseholdIncomeDifferenceReason
                                    ]
                                ]
                            }
                            replacements = { { filerName } }
                        />
                    </div>
                );
            });
        };

        return (
            <SummaryFilter summaryId = { SsapSummaryType.INCOME_DISCREPANCY } >
                <div
                    className = 'summary__block margin-b-20'
                    key = 'tax_household_summary_block'
                >
                    <div className = 'summary__header'>
                        <SubHeader
                            text = 'ssap.subheader.income_discrepancy'
                            headerClassName = 'gi-heading-alt'
                            additionalElement = {
                                (
                                    <ButtonContainer
                                        type = 'editButton_secondary'
                                        args = { [ undefined, PAGES.INCOME_DISCREPANCY_PAGE ] }
                                        ariaLabel={intlApi.formatMessage({ id: 'ssap.edit.income_discrepancy' })}
                                    />
                                )
                            }
                            additionalElementClassName = 'header-btn'
                        />
                    </div>
                    {
                        getHhmsWithTaxHouseholdIncomeDiscrepancies()
                    }
                </div>
            </SummaryFilter>
        );
    };

    return (
        <SummaryFilter summaryId = { SsapSummaryType.INCOME_INFORMATION.processReplacements({ idx: 'ROOT' }) }>
            <div className = 'final-review-heading' >
                <PageHeader
                    text = 'ssap.subheader.income_info'
                    headerClassName = 'gi-heading-alt'
                />
            </div>
            { isIncomeSummaryPage && renderStudentLoanInterestHelpBanner() }
            <SummaryFilter summaryId = { SsapSummaryType.UNEMPLOYMENT_ATTESTATION } indexMapper = { indexMapper }>
                {
                    isUnemploymentIncomeAttestationEnabled &&

                    <div className = 'summary__block margin-b-20' >
                        <div className = 'grid-row' >
                            <SummaryItem
                                category = 'ssap.income_information.summary.unemployment_income'
                                value = { unemploymentIncome }
                                replacements = { { coverageYear } }
                                noPriorDataValues = { [ null ] }
                                desc = {
                                    [
                                        [
                                            'taxHousehold.unemploymentIncome'
                                        ]
                                    ]
                                }
                            />
                        </div>
                    </div>
                }
            </SummaryFilter>
            { isReviewAndConfirmPage && renderStudentLoanInterestHelpBanner() }
            { getHouseholdMembersSummaries() }
            { getTaxHouseholdDiscrepancyBlock() }
        </SummaryFilter>
    );
};

export default IncomeInformationSummary;
